import { useState, useCallback, useRef } from 'react';
import { translate } from '../i18n';
import emailService from '../services/emailService';
import { notify } from '../components/CustomToast';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB — matches backend max:20480

export const useEmailComposer = (accountId) => {
    const [isSending, setIsSending] = useState(false);

    // --- Attachment helpers ---

    /**
     * Upload a single file to temp storage. Returns the temp_upload object or null on failure.
     * onProgress(percent: number) — called with 0-100 as the upload progresses.
     */
    const uploadTempFile = useCallback(async (file, onProgress, abortSignal) => {
        // Client-side size guard
        if (file.size > MAX_FILE_SIZE) {
            notify('error', `${file.name} ${translate('messages.email.file_too_large')}`);
            return null;
        }

        try {
            const response = await emailService.uploadTempFile(
                file,
                (progressEvent) => {
                    if (progressEvent.total) {
                        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress?.(pct);
                    }
                },
                abortSignal,
            );
            // api interceptor returns response.data directly.
            // The endpoint returns the upload object flat in data: { id, original_filename, file_size, ... }
            return response?.data ?? null;
        } catch (err) {
            if (err?.code === 'ERR_CANCELED') return null; // user aborted — silent
            console.error('Temp upload failed', err);
            notify('error', err?.response?.data?.message || `${translate('messages.email.upload_failed')} ${file.name}`);
            return null;
        }
    }, []);

    // --- Email actions ---

    const sendEmail = useCallback(async (data) => {
        if (!accountId) {
            notify('error', translate('messages.email.no_account'));
            return null;
        }

        try {
            setIsSending(true);
            const response = await emailService.compose(accountId, data);
            notify('success', translate('messages.email.sent_success'));
            return response?.data?.email;
        } catch (error) {
            console.error('Failed to send email', error);
            throw error; // propagate to component for field-level error display
        } finally {
            setIsSending(false);
        }
    }, [accountId]);

    const saveDraft = useCallback(async (data) => {
        if (!accountId) {
            notify('error', translate('messages.email.no_account'));
            return null;
        }

        try {
            setIsSending(true);
            const response = await emailService.compose(accountId, { ...data, is_draft: true });
            notify('success', translate('messages.email.draft_saved'));
            return response?.data?.email;
        } catch (error) {
            console.error('Failed to save draft', error);
            throw error;
        } finally {
            setIsSending(false);
        }
    }, [accountId]);

    const replyEmail = useCallback(async (emailId, data, replyAll = false) => {
        try {
            setIsSending(true);
            const response = await emailService.reply(emailId, { ...data, reply_all: replyAll });
            notify('success', translate('messages.email.reply_sent'));
            return response?.data?.email;
        } catch (error) {
            console.error('Failed to send reply', error);
            throw error;
        } finally {
            setIsSending(false);
        }
    }, []);

    const forwardEmail = useCallback(async (emailId, data) => {
        try {
            setIsSending(true);
            const response = await emailService.forward(emailId, data);
            notify('success', translate('messages.email.forwarded'));
            return response?.data?.email;
        } catch (error) {
            console.error('Failed to forward email', error);
            throw error;
        } finally {
            setIsSending(false);
        }
    }, []);

    /**
     * H9: Update an existing draft with new content, then send it.
     * This avoids creating a duplicate email when editing/sending a draft.
     */
    const sendExistingDraft = useCallback(async (draftId, data) => {
        try {
            setIsSending(true);
            // First update the draft content
            await emailService.updateDraft(draftId, data);
            // Then dispatch it for sending
            const response = await emailService.sendDraft(draftId);
            notify('success', translate('messages.email.sent_success'));
            return response?.data?.email;
        } catch (error) {
            console.error('Failed to send draft', error);
            throw error;
        } finally {
            setIsSending(false);
        }
    }, []);

    return {
        isSending,
        sendEmail,
        saveDraft,
        replyEmail,
        forwardEmail,
        sendExistingDraft,
        uploadTempFile,
    };
};

export default useEmailComposer;
